// SceneryShaders — Set13 P5: the quad + shadow GLSL for the 2D impostor render (§3.6).
//
// TWO passes, in this order (renderOrder within the transparent pass):
//   1. SHADOW pass — a separate quad per instance, ANCHORED at the tree base and ELONGATED
//      down-light (`shearLen = variantMaxZ * cot(alt)`, the same `u_lightDir` the terrain uses, so
//      shadows and hillshade always agree), multiply-blended, DEPTH-WRITE OFF. Multiply is
//      order-independent, so no sorting. The shadow shape is the BAKED CROWN's alpha mask — the
//      shadow MATCHES the tree's real silhouette and resolved size (the old analytic stretched-oval
//      matched nothing and froze at the authored `footprintRadius`), and it re-orients with the
//      light for free without a shadow-specific atlas. Shadows before trees ⇒ they land on terrain
//      only (§3.6's accepted consequence).
//   2. TREE pass — alpha-test (not alpha-blend) + DEPTH-WRITE ON (§4.7). The depth key is
//      `variantMaxZ * scale` (the baked crown's measured top)
//      folded into the quad's z, so the taller crown occludes the shorter with no sorting and no
//      terrain data (§4.5).
//
// Both mirror the terrain conventions (§4.8): the same `u_terrainRegions` AABB clip (fragments
// outside every active TerrainRegion render at alpha 0 — no trees floating over clipped terrain)
// and the same `u_opacity` (they fade together).
//
// ShaderParse conventions (plan §8): NO `#version` header (three.js prepends its own), no
// backticks inside the template literal, no GLSL-ES-300 reserved identifiers (`flat`, `smooth`,
// `noperspective`, `centroid`, …). Both are asserted by SceneryShaders.test.ts.
import {TERRAIN_REGION_CLIP_MAX} from "../../Materials/Compose/ShaderAssembler.js";

/** Shared terrain-REGION clip: zeroes a fragment outside every active TerrainRegion AABB
 *  (mirrors the material shader's `terrainRegionClip`). count 0 = no clip. */
const SCENERY_REGION_CLIP_GLSL = `
uniform int u_terrainRegionCount;
uniform vec4 u_terrainRegions[${TERRAIN_REGION_CLIP_MAX}];
`;

// ── TREE (quad) pass — alpha-test + depth-write on ───────────────────────────────────────────
// The quad is a unit plane (PlaneGeometry(2,2)); `instanceMatrix` carries world position
// (x,y), per-instance scale (= footprintRadius * jitterScale) and rotation. The vertex shader
// lifts the quad to z = u_terrainZ + instanceDepthKey * u_depthScale so the depth buffer orders
// overlapping crowns by crown-top height (the §3.6 depth key) — in top-down ortho, screen XY =
// world XY, so elevation is irrelevant; only overlap order matters, and this key provides it
// with zero terrain data (§4.5).
export const TREE_VERTEX_GLSL = `
precision highp float;
uniform float u_atlasCols;
uniform float u_atlasRows;
uniform float u_terrainZ;
uniform float u_depthScale;
attribute float instanceVariant;
attribute float instanceDepthKey;
attribute float instanceTint;
varying vec2 vCellUV;
varying vec2 vWorldXY;
varying float vTint;
void main() {
	vec4 wp = instanceMatrix * vec4(position, 1.0);
	// Crown-top ordering: taller (larger depthKey) → higher z → in front (camera looks down -z).
	wp.z = u_terrainZ + instanceDepthKey * u_depthScale;
	// Select this instance's atlas cell from its variant index (cell = (i%cols, floor(i/cols))).
	// INTEGER arithmetic (2026-08-28): the old float col = mod(v, u_atlasCols); float row =
	// floor(v/u_atlasCols); was GPU-DEPENDENT — GLSL ES does not guarantee exact float division, and
	// on hardware GPUs 3.0/3.0 can evaluate to 0.99999... → floor(...) = 0 → col=3,row=0 → vCellUV.x
	// in [1,1.33] → off-atlas → samples transparent padding → discarded → whole trees vanish
	// (SwiftShader used exact division so it never hit this). Integer division is exact everywhere.
	// (Root-cause identified on 2026-08-28.)
	int vi = int(instanceVariant + 0.5);
	int cols = int(u_atlasCols + 0.5);
	int rows = int(u_atlasRows + 0.5);
	int c = vi - (vi / cols) * cols;
	int r = vi / cols;
	float col = float(c);
	float row = float(r);
	// Defensive clamp: a future rounding slip degrades to "wrong variant", never "invisible tree".
	col = clamp(col, 0.0, u_atlasCols - 1.0);
	row = clamp(row, 0.0, u_atlasRows - 1.0);
	vCellUV = vec2((col + uv.x) / u_atlasCols, (row + uv.y) / u_atlasRows);
	vWorldXY = wp.xy;
	vTint = instanceTint;
	gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

// ── Set15 P4 (§3.8) — the 3D CYLINDRICAL BILLBOARD pass ─────────────────────────────────────
// A single InstancedMesh per kind drawing vertical billboards textured with the PITCH-BAKED atlas
// (the tree's true side view at the locked viewpoint's pitch). The geometry is a VERTICAL quad in
// the XZ plane (positions (±1, 0, ±1)); the instanceMatrix carries:
//   - translation (x, y, baseZ + cellHalfExtent) — baseZ = the terrain height at the tree base,
//     so the cell bottom (the tree's ground in the pitched bake) sits exactly on the surface;
//   - rotation about +z = the camera-facing azimuth (a CYLINDRICAL billboard: rotates about z
//     only, so trunks stay vertical — world up stays +z);
//   - scale (scl, scl, scl) so the square cell spans ±scl both horizontally and vertically.
// NO depth key: the crown-top z-lift is GONE. In 3D the depth buffer does the occlusion correctly
// (the top-down ortho had no real depth, which is the only reason the §3.6 depth key existed).
// The per-instance world position + matrix already sit at the true world z; the fragment reuses
// TREE_FRAGMENT_GLSL (same varyings, same region clip, same alpha-test + output transfer).
export const BILLBOARD3D_VERTEX_GLSL = `
precision highp float;
uniform float u_atlasCols;
uniform float u_atlasRows;
attribute float instanceVariant;
attribute float instanceTint;
varying vec2 vCellUV;
varying vec2 vWorldXY;
varying float vTint;
void main() {
	vec4 wp = instanceMatrix * vec4(position, 1.0);
	// Select this instance's atlas cell from its variant index — INTEGER arithmetic (see the tree VS
	// for the full explanation: float div made cell selection GPU-dependent; integer div is exact).
	int vi = int(instanceVariant + 0.5);
	int cols = int(u_atlasCols + 0.5);
	int rows = int(u_atlasRows + 0.5);
	int c = vi - (vi / cols) * cols;
	int r = vi / cols;
	float col = clamp(float(c), 0.0, u_atlasCols - 1.0);
	float row = clamp(float(r), 0.0, u_atlasRows - 1.0);
	vCellUV = vec2((col + uv.x) / u_atlasCols, (row + uv.y) / u_atlasRows);
	vWorldXY = wp.xy;
	vTint = instanceTint;
	gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

export const TREE_FRAGMENT_GLSL = `
precision highp float;
uniform sampler2D u_atlas;
uniform float u_opacity;
uniform float u_texelX;
uniform float u_texelY;
uniform int u_terrainRegionCount;
uniform vec4 u_terrainRegions[${TERRAIN_REGION_CLIP_MAX}];
varying vec2 vCellUV;
varying vec2 vWorldXY;
varying float vTint;
void main() {
	if (u_terrainRegionCount > 0) {
		bool inside = false;
		for (int i = 0; i < ${TERRAIN_REGION_CLIP_MAX}; i++) {
			if (i >= u_terrainRegionCount) break;
			vec4 r = u_terrainRegions[i];
			if (vWorldXY.x >= r.x && vWorldXY.x <= r.z && vWorldXY.y >= r.y && vWorldXY.y <= r.w) { inside = true; break; }
		}
		if (!inside) discard;
	}
	// Half-texel inset (in atlas-uv units) so magFilter=LinearFilter never bleeds a half-texel of the
	// NEIGHBOURING variant into this quad's edge at the cell boundary (the shadow pass already did
	// this; the tree pass now matches — 2026-08-28 hardening).
	vec2 inset = vec2(u_texelX, u_texelY);
	vec4 c = texture(u_atlas, clamp(vCellUV, inset, vec2(1.0) - inset));
	// Hard edge (§4.7): alpha-test rather than blend. The material's alphaTest also discards;
	// this guard is belt-and-braces so the shader is self-contained.
	if (c.a < 0.5) discard;
	// Per-instance albedo tint (the free jitter — cheapest per-tree colour variation).
	gl_FragColor = vec4(c.rgb * vTint, c.a * u_opacity);
	// Set14 P7 §4.11 REVISED (2026-08-17) — the OUTPUT TRANSFER CHAIN, applied at the FINAL
	// display write (the tree quad draws to the DEFAULT framebuffer, where currentRenderTarget
	// is null). The baked atlas is LINEAR (three forces NoToneMapping + a linear output colour
	// space for ANY non-XR render target — WebGLPrograms.js; there is no opt-out), so the quad
	// MUST apply the transfer here or the linear values display as if sRGB → severe darkening
	// concentrated in low/mid tones (the near-black-leaves bug). three injects toneMapping()
	// (only when the renderer's toneMapping != NoToneMapping — hence the #ifdef TONE_MAPPING
	// guard) and linearToOutputTexel() (unconditionally) into the prefix per the renderer's
	// CURRENT settings, so calling them keeps the MapSettings tone-mapping live. Runs AFTER the
	// discards (clipped/alpha-tested fragments exit before the transfer).
	#ifdef TONE_MAPPING
	gl_FragColor.rgb = toneMapping(gl_FragColor.rgb);
	#endif
	gl_FragColor = linearToOutputTexel(gl_FragColor);
}
`;

// ── SHADOW pass — multiply blend, depth-write off, order-independent ─────────────────────────
// The shadow is the BAKED CROWN's alpha mask, ANCHORED at the tree base and ELONGATED down-light
// so it matches the tree's real silhouette + resolved size. The quad is scaled to the crown
// half-extent (`cellWorldHalfExtent * scale` — the SAME number the tree quad uses, so it can't
// drift), ROTATED so its +y aligns with the down-light direction, and sized along the light to
// cover the crown PLUS its shear tail (`crownHalf + shearLen`).
//
// The fragment blends a CONTACT crown silhouette (anchored at the base) with a copy DISPLACED
// down-light by the full shear (the "long shadow" cast by the tall apex). The max-union is stable
// (no inversion), anchored, correct-sized (both sample the crown at the same crownHalf the tree
// quad uses) and reads as a directional elongated shadow. Because the shear is computed from
// `u_lightDir` at draw time, the shadow re-orients with the light with no shadow-specific atlas
// re-bake. (This fixes the earlier single-height SHIFT, which displaced the whole crown-disc by
// `shearLen` → a floating detached shadow, invisible/off-screen.)
//
// ⚠ Multiply-blend semantics: three.js MultiplyBlending does `result = src.rgb * dst.rgb` (the
// COLOUR, not alpha, is the multiplier). To leave un-shadowed fragments UNCHANGED the fragment
// must emit src.rgb = 1 there, and src.rgb = <dark tint> where shaded — so the output is
// `mix(vec3(1), vec3(u_shadowTint), shade)` with alpha 1. Emitting alpha as the "shade" would
// darken everything by the tint (the classic multiply-blend footgun).
export const SHADOW_VERTEX_GLSL = `
precision highp float;
uniform float u_terrainZ;
uniform float u_atlasCols;
uniform float u_atlasRows;
uniform vec3 u_lightDir;
attribute float instanceVariant;
attribute float instanceCrownHalf;
attribute float instanceShearLen;
varying vec2 vWorldXY;
varying vec2 vTreeOrigin;
varying float vCellCol;
varying float vCellRow;
varying float vCrownHalf;
varying float vShearLen;
void main() {
	vec4 wp = instanceMatrix * vec4(position, 1.0);
	// The quad centre is offset shearLen/2 DOWN-LIGHT from the tree base (see fillKindInstances:
	// the asymmetric rect spans [−crownHalf, +crownHalf+shearLen] along the light). Recover the
	// TREE ORIGIN by stepping back up-light from the quad centre by half the shear, so the
	// fragment measures ground offsets from the tree base (along = 0 at the base → the anchored
	// crown sits at the tree, and the tail extends down-light).
	vec4 origin = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
	vTreeOrigin = origin.xy - 0.5 * instanceShearLen * normalize(u_lightDir.xy);
	wp.z = u_terrainZ;
	vWorldXY = wp.xy;
	// Cell select — INTEGER arithmetic (see the tree VS: float div was GPU-dependent → off-atlas UV
	// → discarded trees on hardware GPUs; integer div is exact everywhere). Shadow uses vCellCol/Row.
	int vi = int(instanceVariant + 0.5);
	int cols = int(u_atlasCols + 0.5);
	int rows = int(u_atlasRows + 0.5);
	vCellCol = clamp(float(vi - (vi / cols) * cols), 0.0, u_atlasCols - 1.0);
	vCellRow = clamp(float(vi / cols), 0.0, u_atlasRows - 1.0);
	vCrownHalf = instanceCrownHalf;
	vShearLen = instanceShearLen;
	gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

export const SHADOW_FRAGMENT_GLSL = `
precision highp float;
uniform sampler2D u_atlas;
uniform float u_atlasCols;
uniform float u_atlasRows;
uniform float u_shadowStrength;
uniform float u_texelX;
uniform float u_texelY;
uniform vec3 u_lightDir;
uniform vec3 u_shadowTint;
uniform int u_terrainRegionCount;
uniform vec4 u_terrainRegions[${TERRAIN_REGION_CLIP_MAX}];
varying vec2 vWorldXY;
varying vec2 vTreeOrigin;
varying float vCellCol;
varying float vCellRow;
varying float vCrownHalf;
varying float vShearLen;
// The crown's baked alpha in this atlas cell, with a soft edge (a few taps) so the shadow reads
// as soft rather than a hard stencil copy of the sprite.
//
// ⚠ CELL-CONTAINMENT: the shadow samples the crown at a *cell-local* UV. Without a bounds guard,
// an out-of-range uv maps (via ClampToEdge, which only clamps at the ATLAS edge) into the
// NEIGHBOURING cell — another variant's crown — painting phantom "second/third shadows" near the
// tree (each a different variant, hence different shape/size). The shadow quad is sized so a
// correct fragment mapping lands uv in [0,1]² exactly, but a bounds guard makes ANY future
// geometry/framing mismatch degrade to a short clean shadow instead of bleeding other variants in.
float crownAlpha(vec2 uv) {
	// Outside this instance's own atlas cell → NO crown (reject, not clamp: clamping would pull
	// in the neighbour cell's edge texels).
	if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
	// Inset by one texel (in cell-uv units) so the 4-tap soft blur below stays INSIDE the cell too
	// — the +/- texel taps at the cell border must not reach into the neighbouring variant.
	vec2 cellUvInset = vec2(u_texelX * u_atlasCols, u_texelY * u_atlasRows);
	vec2 uvC = clamp(uv, cellUvInset, vec2(1.0) - cellUvInset);
	vec2 cell = vec2((vCellCol + uvC.x) / u_atlasCols, (vCellRow + uvC.y) / u_atlasRows);
	float a = texture(u_atlas, cell).a;
	float soft = (texture(u_atlas, cell + vec2(u_texelX, 0.0)).a
		+ texture(u_atlas, cell + vec2(-u_texelX, 0.0)).a
		+ texture(u_atlas, cell + vec2(0.0, u_texelY)).a
		+ texture(u_atlas, cell + vec2(0.0, -u_texelY)).a) * 0.25;
	return clamp(a + (soft - a) * 0.5, 0.0, 1.0);
}
void main() {
	if (u_terrainRegionCount > 0) {
		bool inside = false;
		for (int i = 0; i < ${TERRAIN_REGION_CLIP_MAX}; i++) {
			if (i >= u_terrainRegionCount) break;
			vec4 r = u_terrainRegions[i];
			if (vWorldXY.x >= r.x && vWorldXY.x <= r.z && vWorldXY.y >= r.y && vWorldXY.y <= r.w) { inside = true; break; }
		}
		if (!inside) discard;
	}
	vec2 D = normalize(u_lightDir.xy); // down-light (away from sun) — the direction shadows extend
	vec2 q = vWorldXY - vTreeOrigin;   // metres, relative to the tree base, world-aligned
	float along = dot(q, D);           // metres along the down-light direction (0 at the base)
	// Anchor the shadow at the base and ELONGATE it down-light: sample the crown at g, where the
	// up-light half (along<0) is un-sheared (g=q, anchored) and the down-light half is stretched
	// so its edge reaches crownHalf + shearLen instead of crownHalf. This is stable (no
	// inversion — a single clamp), anchored, correct-sized (same crownHalf as the tree quad) and
	// produces a real directional tail (down extent = crownHalf+shearLen, up extent = crownHalf).
	vec2 g = q - clamp(along / max(vCrownHalf + vShearLen, 1e-4), 0.0, 1.0) * vShearLen * D;
	vec2 uv = g / (2.0 * max(vCrownHalf, 1e-4)) + 0.5;
	float shade = crownAlpha(uv) * u_shadowStrength;
	// Multiply-blend multiplier colour: 1.0 where un-shadowed (unchanged dst), dark tint where
	// shaded (§ above). Spec-clean smoothstep: edge0 < edge1.
	gl_FragColor = vec4(mix(vec3(1.0), u_shadowTint, shade), 1.0);
}
`;
